import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Alert,
  Snackbar,
} from '@mui/material';
import { Check, Star } from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

const PricingPage = () => {
  const navigate = useNavigate();
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState('');

  const handleBuy = (plan) => {
    // In a real application, this would redirect to a payment processor
    // For demo purposes, we're simulating a successful payment
    setPaymentStatus(`Thanks for purchasing the ${plan} plan! Your account has been upgraded.`);
    setOpenSnackbar(true);
    
    // In a real app, we would set the user's premium status in a database
    // For now, we'll just store it in localStorage for demo purposes
    localStorage.setItem('userPremiumStatus', 'true');
    
    // Redirect to CV generation after a delay
    setTimeout(() => {
      navigate('/manual');
    }, 3000);
  };

  const pricingPlans = [
    {
      title: 'Free',
      price: '€0',
      period: '',
      features: [
        'Generate Basic CV',
        'Professional Summary',
        'Limited Work Experience Section',
        'Basic Education Section',
        'Limited Skills Section',
        'Modern Template Only',
      ],
      limitations: [
        'Limited to 3 CV generations',
        'CV sections partially hidden',
        'No premium templates',
        'No export options',
      ],
      buttonText: 'Current Plan',
      disabled: true,
      highlighted: false,
    },
    {
      title: 'Premium',
      price: '€9.99',
      period: '/month',
      features: [
        'Full CV Generation',
        'All CV Sections Visible',
        'Unlimited Work Experience',
        'Detailed Education History',
        'Comprehensive Skills Section',
        'Certifications & Languages',
        'Projects & Memberships',
        'References Section',
        'All Premium Templates',
        'PDF, Word, and Markdown Export',
        'ATS Optimization',
        'Unlimited CV Generations',
      ],
      buttonText: 'Upgrade Now',
      highlighted: true,
    },
    {
      title: 'Business',
      price: '€29.99',
      period: '/month',
      features: [
        'Everything in Premium',
        'Priority Support',
        'Team Management',
        'Custom Branding',
        'LinkedIn Integration',
        'Job Matching',
        'Interview Preparation',
        'Career Coaching',
        'Bulk CV Generation',
      ],
      buttonText: 'Contact Sales',
      highlighted: false,
    },
  ];

  return (
    <Box 
      sx={{ 
        py: 8,
        background: 'linear-gradient(180deg, #f5f7fa 0%, #e4e9f0 100%)'
      }}
      component={motion.div}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography 
            variant="h2" 
            gutterBottom
            component={motion.div}
            initial={{ y: -20 }}
            animate={{ y: 0 }}
            transition={{ duration: 0.5 }}
          >
            Unlock Your Professional CV
          </Typography>
          <Typography
            variant="h5"
            color="text.secondary"
            sx={{ mb: 3 }}
            component={motion.div}
            initial={{ y: -10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            Choose the right plan to showcase your professional experience
          </Typography>
          <Divider sx={{ maxWidth: '200px', mx: 'auto', mb: 4 }} />
        </Box>

        <Grid 
          container 
          spacing={4} 
          justifyContent="center" 
          alignItems="stretch"
          component={motion.div}
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          {pricingPlans.map((plan, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Card
                component={motion.div}
                whileHover={plan.highlighted ? { scale: 1.03 } : { scale: 1.02 }}
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 3,
                  overflow: 'hidden',
                  boxShadow: plan.highlighted 
                    ? '0 10px 30px rgba(84, 105, 212, 0.5)' 
                    : '0 5px 15px rgba(0, 0, 0, 0.08)',
                  ...(plan.highlighted && {
                    border: 2,
                    borderColor: 'primary.main',
                    position: 'relative'
                  }),
                }}
              >
                {plan.highlighted && (
                  <Box 
                    sx={{ 
                      position: 'absolute', 
                      top: 0, 
                      right: 0, 
                      backgroundColor: 'primary.main',
                      color: 'white',
                      py: 0.5,
                      px: 2,
                      borderBottomLeftRadius: 8,
                      fontSize: '0.75rem',
                      fontWeight: 'bold'
                    }}
                  >
                    MOST POPULAR
                  </Box>
                )}
                <CardHeader
                  title={plan.title}
                  titleTypographyProps={{ 
                    align: 'center', 
                    variant: 'h4',
                    fontWeight: 700
                  }}
                  sx={{
                    bgcolor: plan.highlighted ? 'primary.main' : 'grey.100',
                    color: plan.highlighted ? 'white' : 'inherit',
                    py: 3
                  }}
                  avatar={plan.highlighted ? <Star sx={{ color: 'white' }} /> : null}
                />
                <CardContent sx={{ flexGrow: 1, p: 3 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'baseline',
                      mb: 3,
                      textAlign: 'center',
                    }}
                  >
                    <Typography variant="h3" component="span" fontWeight="bold">
                      {plan.price}
                    </Typography>
                    <Typography variant="subtitle1" color="text.secondary" component="span" sx={{ ml: 1 }}>
                      {plan.period}
                    </Typography>
                  </Box>
                  
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Features:
                  </Typography>
                  <List sx={{ mb: 2 }}>
                    {plan.features.map((feature, featureIndex) => (
                      <ListItem key={featureIndex} sx={{ py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 30 }}>
                          <Check color={plan.highlighted ? "primary" : "action"} />
                        </ListItemIcon>
                        <ListItemText 
                          primary={feature} 
                          primaryTypographyProps={{ 
                            variant: 'body2',
                            fontWeight: feature.includes('All') || feature.includes('Unlimited') ? 700 : 400
                          }} 
                        />
                      </ListItem>
                    ))}
                  </List>
                  
                  {plan.limitations && (
                    <>
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom color="text.secondary">
                        Limitations:
                      </Typography>
                      <List>
                        {plan.limitations.map((limitation, limitIndex) => (
                          <ListItem key={limitIndex} sx={{ py: 0.5 }}>
                            <ListItemText 
                              primary={limitation} 
                              primaryTypographyProps={{ 
                                variant: 'body2',
                                color: 'text.secondary'
                              }} 
                            />
                          </ListItem>
                        ))}
                      </List>
                    </>
                  )}
                  
                  <Button
                    fullWidth
                    variant={plan.highlighted ? 'contained' : 'outlined'}
                    color="primary"
                    size="large"
                    sx={{ 
                      mt: 3, 
                      py: 1.5, 
                      borderRadius: 2,
                      fontWeight: 'bold'
                    }}
                    onClick={() => handleBuy(plan.title)}
                    disabled={plan.disabled}
                  >
                    {plan.buttonText}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Box 
          sx={{ 
            mt: 10, 
            textAlign: 'center',
            p: 5,
            bgcolor: 'rgba(255,255,255,0.8)',
            borderRadius: 3,
            boxShadow: '0 5px 15px rgba(0,0,0,0.05)'
          }}
          component={motion.div}
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.6 }}
        >
          <Typography variant="h4" gutterBottom fontWeight="bold">
            Need a Custom Enterprise Solution?
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3, maxWidth: '700px', mx: 'auto' }}>
            Perfect for recruiting agencies, HR departments, and career service centers.
            Get volume discounts, custom branding, and dedicated support.
          </Typography>
          <Button
            variant="contained"
            color="secondary"
            size="large"
            href="mailto:enterprise@clevercv.com"
            sx={{ 
              py: 1.5, 
              px: 4, 
              borderRadius: 2,
              fontWeight: 'bold'
            }}
          >
            Contact Enterprise Sales
          </Button>
        </Box>
      </Container>
      
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setOpenSnackbar(false)} 
          severity="success" 
          sx={{ width: '100%' }}
        >
          {paymentStatus}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PricingPage; 